/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import React, {useCallback, useEffect, useState} from 'react';
import appTheme from '../../common/theme/appTheme.json';
import {Box, Button} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {GridActionsCellItem, GridColDef} from '@mui/x-data-grid';
import CustomDataGrid from '../../common/data_grid/CustomDataGrid';
import {shallowEqual, useDispatch, useSelector} from 'react-redux';
import ModalDialog from '../../common/dialogs/ModalDialog';
import WarningIcon from '@mui/icons-material/Warning';
import {Link, useRouteMatch} from 'react-router-dom';
import ErrorMessage from '../../common/layout/ErrorMessage';
import {StyledProvisioningPageHeading} from '../StyledProvisioningPageHeading';
import ModalLoadingSpinner from '../../common/layout/ModalLoadingSpinner';
import {selectContracts, selectContractsRequestError, selectIsRequestingContracts} from '../../../store';
import CreateIcon from '@mui/icons-material/Create';
import {TopAlignedChipCell} from '../../common/data_grid/ChipCell';
import {RenderTopAlignedFixedHeightCell} from '../../common/data_grid/TopAlignedCell';
import {deleteContract, requestContracts} from '../../../store/contracts/actions';
import Contract from '../../../shared/entities/contract';

interface ContractsTableProps {
  // Required for displaying tables in unit tests
  disableVirtualization?: boolean;
}

const ContractsTable = ({disableVirtualization = false}: ContractsTableProps) => {
  const dispatch = useDispatch();
  const {path} = useRouteMatch();
  const loadingContracts = useSelector(selectIsRequestingContracts, shallowEqual);
  const contractsArray = useSelector(selectContracts, shallowEqual);
  const contractsRequestError = useSelector(selectContractsRequestError, shallowEqual);
  const [deleteModalTarget, setDeleteModalTarget] = useState<Contract | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    dispatch(requestContracts());
  }, [dispatch]);

  const COLLAPSED_ROW_HEIGHT_PX = 49;
  const columns: GridColDef[] = [
    {
      field: 'contractName',
      headerName: 'Contract name',
      width: 260,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'startDate',
      headerName: 'Contract start date',
      width: 180,
      renderCell: params => {
        params.value = params.value.split('T')[0];
        return RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX)(params);
      },
    },
    {
      field: 'endDate',
      headerName: 'Contract end date',
      width: 180,
      renderCell: params => {
        params.value = params.value.split('T')[0];
        return RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX)(params);
      },
    },
    {
      field: 'clin',
      headerName: 'CLIN',
      width: 160,
      renderCell: params => {
        params.value = params.value.split(' ')[0];
        return RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX)(params);
      },
    },
    {
      field: 'simops',
      headerName: 'SIMOPS',
      width: 160,
      renderCell: RenderTopAlignedFixedHeightCell(COLLAPSED_ROW_HEIGHT_PX),
    },
    {
      field: 'terminals',
      headerName: 'Terminals',
      flex: 1,
      renderCell: params => {
        return <TopAlignedChipCell params={params}/>;
      },
      sortable: false,
    },
    {
      field: 'actions',
      headerName: '',
      type: 'actions',
      width: 160,
      align: 'right',
      cellClassName: 'ActionsCell-TopAligned',
      getActions: params => [
        <GridActionsCellItem
          key={'create-contract'}
          icon={<CreateIcon/>}
          component={Link}
          to={`${path}/edit/${params.row.id}`}
        />,
        <GridActionsCellItem
          key={'delete-contract'}
          icon={<DeleteIcon/>}
          onClick={() => {
            setDeleteModalTarget(params.row);
            setShowDeleteModal(true);
          }}
        />,
      ],
    },
  ];

  const AddContractButton = useCallback(
    () => (
      <Button
        component={Link}
        to={`${path}/new`}
        startIcon={<AddIcon/>}
        variant="contained"
        sx={{
          marginLeft: 'auto',
          color: '#000000',
          letterSpacing: '0.8px',
          backgroundColor: appTheme.mptLightBlue,
          '&:hover': {
            backgroundColor: appTheme.mptLightBlue,
            svg: {
              color: '#000000',
            },
          },
        }}
      >
        Provision new contract
      </Button>
    ),
    [path],
  );

  return (
    <>
      {contractsRequestError ? <ErrorMessage>{contractsRequestError}</ErrorMessage> : null}

      <StyledProvisioningPageHeading>
        Contracts {contractsArray ? `(${contractsArray.length})` : ''}
      </StyledProvisioningPageHeading>

      {loadingContracts ? <ModalLoadingSpinner/> : null}

      <Box display="flex" justifyContent="space-between" flexDirection={'column'} height={'100%'}>
        <CustomDataGrid
          rows={contractsArray || []}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => COLLAPSED_ROW_HEIGHT_PX}
          columns={columns}
          initialState={{
            sorting: {
              sortModel: [{field: 'contractName', sort: 'asc'}],
            },
          }}
          toolbarActionButtons={[<AddContractButton key={'add-contract-button'}/>]}
          slotProps={{
            toolbar: {
              csvOptions: {
                fileName: 'Contracts',
              },
            },
          }}
          disableVirtualization={disableVirtualization}
        />
      </Box>

      <ModalDialog
        open={showDeleteModal}
        onLeftBtnClicked={() => {
          setShowDeleteModal(false);
        }}
        onRightBtnClicked={() => {
          dispatch(deleteContract(deleteModalTarget!.id));
          setShowDeleteModal(false);
        }}
        onExited={() => {
          // Remove the delete target after the dialog's exit animation has finished. Otherwise, the `message` below
          // will briefly display `undefined` for the organization's display name
          setDeleteModalTarget(undefined);
        }}
        title={
          <Box display={'flex'} gap={'8px'} alignItems={'center'}>
            <WarningIcon htmlColor={'#FFB029'}/> Confirm Contract Deletion
          </Box>
        }
        message={
          <>
            Are you sure you want to delete the contract <code>{deleteModalTarget?.contractName}</code>?
            {deleteModalTarget && deleteModalTarget.terminals.length > 0 && (<><br/>
              <br/>
              <>{deleteModalTarget?.terminals.length} terminal(s)
                associated with this contract will no longer have a contract assigned to them.
              </>
            </>)}
            <br/>
            <br/>
            This action cannot be undone.
          </>
        }
      />
    </>
  );
};

export default ContractsTable;
